import React, {useEffect, useMemo, useState} from "react";
import {
    AlertOutlined,
    AppstoreAddOutlined,
    ApartmentOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    FileSearchOutlined,
    LoadingOutlined,
    PlusOutlined,
    SettingOutlined,
    TagsOutlined,
    UploadOutlined
} from "@ant-design/icons";
import {AutoComplete, Button, Form, Input, message, Modal, Popconfirm, Popover, Select, Space, Switch, Table, Tabs, Tag, Tooltip, Typography, Upload} from "antd";
import {Formik} from "formik";
import api from "../api";
import {handleApiError} from "../helpers/formatApiError";
import setTitle from "../helpers/setTitle";
import {useTranslation} from "react-i18next";
import EntityAssetFormFields from "../configurable_entities/EntityAssetFormFields";
import ConfigurableEntityIcon, {configurableIconLabel, configurableIconSelectOptions} from "../configurable_entities/ConfigurableEntityIcon";
import {entityAssetFormHasRequiredValues, entityTypeFields, normalizeEntityAssetFormValues} from "../configurable_entities/entityAssetFormPayload";
import AssetSpreadsheetImportSetup, {mappingLabel} from "../configurable_entities/AssetSpreadsheetImportSetup";
import MetadataEntityImportSetup, {metadataEntityMappingLabel, metadataEntityTypeFields} from "../configurable_entities/MetadataEntityImportSetup";
import {STANDARD_METADATA_FIELD_OPTIONS, standardMetadataFieldLabel} from "../configurable_entities/standardFields";
import User from "../helpers/User";
import OrgNavLink from "../helpers/OrgNavLink";
import TimeAgo from "../helpers/TimeAgo";
import useConsumer from "../../channels/consumer";
import AssetGroupChooser from "./AssetGroupChooser";
import useCurrentOrg from "../helpers/useCurrentOrg";
import {useLocation} from "../helpers/routerCompat";

const {Text} = Typography;

const typeConfig = {
    entityType: {
        titleKey: "configurable-text-assets",
        title: "Text Asset Types",
        endpoint: "/api/entity_types",
        payloadKey: "entity_type",
        groupAttr: "entity_type_custom_meta_groups_attributes",
        fieldAttr: "entity_type_custom_meta_fields_attributes",
        groupIdKey: "custom_meta_group_id",
        fieldIdKey: "custom_meta_field_id"
    },
    metadataEntityType: {
        titleKey: "configurable-metadata-entities",
        title: "Metadata Entity Types",
        endpoint: "/api/metadata_entity_types",
        payloadKey: "metadata_entity_type",
        groupAttr: "metadata_entity_type_custom_meta_groups_attributes",
        fieldAttr: "metadata_entity_type_custom_meta_fields_attributes",
        groupIdKey: "custom_meta_group_id",
        fieldIdKey: "custom_meta_field_id"
    },
    group: {
        titleKey: "custom-meta-groups",
        title: "Field Groups",
        endpoint: "/api/custom_meta_groups",
        payloadKey: "custom_meta_group",
        fieldAttr: "custom_meta_group_memberships_attributes",
        fieldIdKey: "custom_meta_field_id"
    },
    assetImport: {
        endpoint: "/api/asset_spreadsheet_imports"
    },
    metadataImport: {
        endpoint: "/api/metadata_entity_imports"
    }
};

const TAB_KEYS = [
    "text-asset-types",
    "metadata-entities",
    "field-groups",
    "text-asset-imports",
    "metadata-entity-imports"
];
const DEFAULT_TAB = TAB_KEYS[0];

export default function ConfigurableEntities() {
    const {t} = useTranslation();
    const currentOrg = useCurrentOrg();
    const location = useLocation();
    const metadataEntitiesEnabled = !!(currentOrg?.configurable_entities_enabled && currentOrg?.metadata_entities_enabled);
    setTitle(t("configurable-assets-and-entities", "Configurable Assets and Entities"));

    const tabFromHash = (locationHash = window.location.hash) => {
        const hash = locationHash.replace("#", "");
        if (!TAB_KEYS.includes(hash)) return DEFAULT_TAB;
        return !metadataEntitiesEnabled && ["metadata-entities", "metadata-entity-imports"].includes(hash) ? DEFAULT_TAB : hash;
    };

    const [tab, setTab] = useState(() => tabFromHash(location.hash));

    const onTabClick = key => {
        setTab(key);
        window.location.hash = key;
    };

    useEffect(() => {
        setTab(tabFromHash(location.hash));
    }, [location.hash, metadataEntitiesEnabled]);

    const consumer = useConsumer();

    useEffect(() => {
        if (!consumer) return;

        const subscription = consumer.subscriptions.create({channel: "UserChannel"}, {
            received: data => {
                if (data.type === "assetSpreadsheetImport" && data.attrs?.id) {
                    setAssetImports(prev => prev.map(assetImport => (
                        assetImport.id === data.attrs.id ? {...assetImport, ...data.attrs} : assetImport
                    )));
                }
                if (data.type === "metadataEntityImport" && data.attrs?.id) {
                    setMetadataEntityImports(prev => prev.map(metadataImport => (
                        metadataImport.id === data.attrs.id ? {...metadataImport, ...data.attrs} : metadataImport
                    )));
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [consumer]);

    const [entityTypes, setEntityTypes] = useState([]);
    const [metadataEntityTypes, setMetadataEntityTypes] = useState([]);
    const [groups, setGroups] = useState([]);
    const [assetImports, setAssetImports] = useState([]);
    const [metadataEntityImports, setMetadataEntityImports] = useState([]);
    const [assetFields, setAssetFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [assetModalOpen, setAssetModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [creatingEntityType, setCreatingEntityType] = useState(null);
    const [creatingAsset, setCreatingAsset] = useState(false);
    const [assetFormSubmittable, setAssetFormSubmittable] = useState(false);
    const [form] = Form.useForm();
    const [assetForm] = Form.useForm();

    const fieldOptions = useMemo(() => {
        return assetFields.map(field => ({
            value: field.id,
            label: `${field.name} (${field.value_type}${field.free ? "" : ", controlled"})`
        }));
    }, [assetFields]);

    const groupOptions = useMemo(() => (
        groups.map(group => ({
            value: group.id,
            label: group.name
        }))
    ), [groups]);

    const loadData = () => {
        setLoading(true);
        Promise.all([
            api.get("/api/entity_types"),
            metadataEntitiesEnabled ? api.get("/api/metadata_entity_types") : Promise.resolve({data: []}),
            api.get("/api/custom_meta_groups"),
            api.get("/api/asset_spreadsheet_imports"),
            metadataEntitiesEnabled ? api.get("/api/metadata_entity_imports") : Promise.resolve({data: []}),
            api.get("/api/custom_meta_fields?owner_type=Asset")
        ]).then(([entityRes, metadataRes, groupRes, importRes, metadataImportRes, assetFieldRes]) => {
            setEntityTypes(entityRes.data);
            setMetadataEntityTypes(metadataRes.data);
            setGroups(groupRes.data);
            setAssetImports(importRes.data);
            setMetadataEntityImports(metadataImportRes.data);
            setAssetFields(assetFieldRes.data);
            setLoading(false);
        }).catch(error => {
            setLoading(false);
            handleApiError(error);
        });
    };

    useEffect(() => {
        loadData();
    }, [currentOrg?.id, metadataEntitiesEnabled]);

    const openEditor = (kind, record = null) => {
        setEditing({kind, record});
        setModalOpen(true);
        form.setFieldsValue({
            name: record?.name || "",
            slug: record?.slug || "",
            icon: record?.icon || "",
            description: record?.description || "",
            enabled: record?.enabled ?? true,
            main_text_editable: record?.main_text_editable ?? record?.display_config?.main_text_editable ?? true,
            group_ids: record?.custom_meta_groups?.map(membership => membership.custom_meta_group.id) || [],
            field_ids: record?.custom_meta_fields?.map(membership => membership.custom_meta_field.id) || [],
            standard_field_keys: record?.standard_field_keys || record?.table_config?.standard_field_keys || []
        });
    };

    const closeEditor = () => {
        setModalOpen(false);
        setEditing(null);
        form.resetFields();
    };

    const openAssetCreator = entityType => {
        setCreatingEntityType(entityType);
        setCreatingAsset(false);
        setAssetFormSubmittable(false);
        setAssetModalOpen(true);
        assetForm.resetFields();
        assetForm.setFieldsValue({filename: "", content: "", custom_meta: {}});
    };

    const closeAssetCreator = () => {
        setAssetModalOpen(false);
        setCreatingEntityType(null);
        setCreatingAsset(false);
        setAssetFormSubmittable(false);
        assetForm.resetFields();
    };

    const saveEditor = () => {
        form.validateFields().then(values => {
            const {kind, record} = editing;
            const config = typeConfig[kind];
            const payload = buildPayload(kind, record, values);
            const request = record?.id ?
                api.put(`${config.endpoint}/${record.id}`, {[config.payloadKey]: payload}) :
                api.post(config.endpoint, {[config.payloadKey]: payload});

            request.then(() => {
                message.success(t("message-configuration-saved", "Configuration saved."));
                closeEditor();
                loadData();
            }).catch(handleApiError);
        });
    };

    const destroyRecord = (kind, record) => {
        const config = typeConfig[kind];
        api.delete(`${config.endpoint}/${record.id}`).then(() => {
            message.success(t("message-configuration-removed", "Configuration removed."));
            loadData();
        }).catch(handleApiError);
    };

    const saveAssetCreator = () => {
        assetForm.validateFields().then(values => {
            setCreatingAsset(true);
            api.post(`${typeConfig.entityType.endpoint}/${creatingEntityType.id}/assets`, {
                asset: normalizeEntityAssetFormValues(values, creatingEntityType)
            }).then(() => {
                message.success(t("configurable-text-asset-created", "Text asset created."));
                closeAssetCreator();
                loadData();
            }).catch(error => {
                setCreatingAsset(false);
                handleApiError(error);
            });
        });
    };

    const updateAssetCreatorSubmittable = () => {
        setAssetFormSubmittable(entityAssetFormHasRequiredValues(assetForm.getFieldsValue(true), creatingEntityType));
    };

    const buildPayload = (kind, record, values) => {
        const payload = {
            name: values.name,
            description: values.description,
            icon: values.icon,
            enabled: values.enabled
        };

        if (kind !== "group") {
            const config = typeConfig[kind];
            if (values.slug?.trim()) payload.slug = values.slug.trim();
            if (kind === "entityType") {
                payload.kind = "configurable_text_asset";
                payload.display_config = {
                    ...(record?.display_config || {}),
                    main_text_editable: values.main_text_editable !== false
                };
            }
            if (kind === "metadataEntityType") {
                payload.standard_field_keys = values.standard_field_keys || [];
            }
            payload[config.groupAttr] = nestedMemberships(
                record?.custom_meta_groups || [],
                values.group_ids || [],
                config.groupIdKey,
                membership => membership.custom_meta_group.id
            );
            payload[config.fieldAttr] = nestedMemberships(
                record?.custom_meta_fields || [],
                values.field_ids || [],
                config.fieldIdKey,
                membership => membership.custom_meta_field.id
            );
        } else {
            payload.custom_meta_group_memberships_attributes = nestedMemberships(
                record?.custom_meta_fields || [],
                values.field_ids || [],
                typeConfig.group.fieldIdKey,
                membership => membership.custom_meta_field.id
            );
        }

        return payload;
    };

    const schemaColumns = (kind) => [
        {
            title: t("name", "Name"),
            dataIndex: "name",
            render: (name, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{name}</Text>
                    {record.description && <Text type="secondary">{record.description}</Text>}
                </Space>
            )
        },
        {title: t("slug", "Slug"), dataIndex: "slug", width: 180},
        {
            title: t("icon", "Icon"),
            dataIndex: "icon",
            width: 120,
            render: (icon, record) => icon ? (
                <Tag>
                    <ConfigurableEntityIcon icon={icon} kind={kind === "metadataEntityType" ? "metadata_entity" : "text_asset"}/>
                    {configurableIconLabel(icon)}
                </Tag>
            ) : null
        },
        {
            title: t("enabled", "Enabled"),
            dataIndex: "enabled",
            width: 90,
            render: enabled => enabled ? t("yes", "Yes") : t("no", "No")
        },
        kind === "entityType" && {
            title: t("main-text", "Main Text"),
            dataIndex: "main_text_editable",
            width: 110,
            render: editable => editable === false ? t("locked", "Locked") : t("editable", "Editable")
        },
        {
            title: t("groups", "Groups"),
            dataIndex: "custom_meta_groups",
            width: 220,
            render: groups => (
                <Space wrap size={[4, 4]}>
                    {groups?.map(membership => (
                        <GroupTag key={membership.id} membership={membership}/>
                    ))}
                </Space>
            )
        },
        {
            title: t("direct-fields", "Direct Fields"),
            dataIndex: "custom_meta_fields",
            width: 120,
            render: fields => fields?.length || 0
        },
        kind === "metadataEntityType" && {
            title: t("standard-fields", "Standard Fields"),
            dataIndex: "standard_field_keys",
            width: 220,
            render: keys => (
                <Space wrap size={[4, 4]}>
                    {(keys || []).map(key => (
                        <Tag key={key}>{standardMetadataFieldLabel(key)}</Tag>
                    ))}
                </Space>
            )
        },
        kind === "entityType" && {
            title: t("assets", "Assets"),
            dataIndex: "asset_count",
            width: 90,
            render: (assetCount, record) => (
                <OrgNavLink to={`/explore?entity_type_id=${record.id}`}>
                    {assetCount || 0}
                </OrgNavLink>
            )
        },
        kind === "metadataEntityType" && {
            title: t("entities", "Entities"),
            dataIndex: "tag_count",
            width: 90,
            render: (tagCount, record) => (
                <OrgNavLink to={`/manage/taxonomy#browse-metadata-entity-${record.id}`}>
                    {tagCount || 0}
                </OrgNavLink>
            )
        },
        actionColumn(kind)
    ].filter(Boolean);

    const groupColumns = [
        {
            title: t("name", "Name"),
            dataIndex: "name",
            sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
            render: (name, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{name}</Text>
                    {record.description && <Text type="secondary">{record.description}</Text>}
                </Space>
            )
        },
        {
            title: t("icon", "Icon"),
            dataIndex: "icon",
            width: 120,
            render: icon => icon ? (
                <Tag>
                    <ConfigurableEntityIcon icon={icon}/>
                    {configurableIconLabel(icon)}
                </Tag>
            ) : null
        },
        {
            title: t("fields", "Fields"),
            dataIndex: "custom_meta_fields",
            render: fields => (
                <Space wrap size={[4, 4]}>
                    {fields?.map(membership => (
                        <Tag key={membership.custom_meta_field.id}>{membership.custom_meta_field.name}</Tag>
                    ))}
                </Space>
            )
        },
        {
            title: t("enabled", "Enabled"),
            dataIndex: "enabled",
            width: 110,
            filters: [
                {text: t("yes", "Yes"), value: true},
                {text: t("no", "No"), value: false}
            ],
            onFilter: (value, record) => !!record.enabled === value,
            sorter: (a, b) => Number(!!a.enabled) - Number(!!b.enabled),
            render: enabled => enabled ? t("yes", "Yes") : t("no", "No")
        },
        actionColumn("group")
    ];

    function actionColumn(kind) {
        return {
            title: "",
            key: "actions",
            width: kind === "entityType" ? 170 : 120,
            render: (_, record) => (
                <Space>
                    {kind === "entityType" && (
                        <Button size="small" icon={<PlusOutlined/>} onClick={() => openAssetCreator(record)}>
                            {t("create", "Create")}
                        </Button>
                    )}
                    <Button size="small" icon={<EditOutlined/>} onClick={() => openEditor(kind, record)}/>
                    <Popconfirm
                        title={t("confirm-remove-configuration", "Remove this configuration?")}
                        onConfirm={() => destroyRecord(kind, record)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined/>}/>
                    </Popconfirm>
                </Space>
            )
        };
    }

    const tabExtra = kind => (
        <Button type="primary" icon={<PlusOutlined/>} onClick={() => openEditor(kind)}>
            {t("new", "New")}
        </Button>
    );

    return (
        <>
            <Tabs
                activeKey={tab}
                onTabClick={onTabClick}
                items={[
                    {
                        key: "text-asset-types",
                        label: <Space><AppstoreAddOutlined/>{t("configurable-text-assets", "Text Asset Types")}</Space>,
                        children: (
                            <ConfigTable
                                data={entityTypes}
                                loading={loading}
                                columns={schemaColumns("entityType")}
                                extra={tabExtra("entityType")}
                            />
                        )
                    },
                    metadataEntitiesEnabled && {
                        key: "metadata-entities",
                        label: <Space><ApartmentOutlined/>{t("metadata-entities", "Metadata Entities")}</Space>,
                        children: (
                            <ConfigTable
                                data={metadataEntityTypes}
                                loading={loading}
                                columns={schemaColumns("metadataEntityType")}
                                extra={tabExtra("metadataEntityType")}
                            />
                        )
                    },
                    {
                        key: "field-groups",
                        label: <Space><TagsOutlined/>{t("field-groups", "Field Groups")}</Space>,
                        children: (
                            <ConfigTable
                                data={groups}
                                loading={loading}
                                columns={groupColumns}
                                extra={tabExtra("group")}
                            />
                        )
                    },
                    {
                        key: "text-asset-imports",
                        label: <Space><UploadOutlined/>{t("text-asset-imports", "Text Asset Imports")}</Space>,
                        children: (
                            <Space direction="vertical" style={{width: "100%"}} size="middle">
                                <div style={{display: "flex", justifyContent: "flex-end"}}>
                                    <NewAssetImportButton
                                        entityTypes={entityTypes}
                                        onCreate={loadData}
                                        onDone={loadData}
                                    />
                                </div>
                                <Table
                                    rowKey="id"
                                    size="middle"
                                    loading={loading}
                                    dataSource={assetImports}
                                    columns={assetImportColumns(t, loadData, entityTypes)}
                                    pagination={false}
                                />
                            </Space>
                        )
                    },
                    metadataEntitiesEnabled && {
                        key: "metadata-entity-imports",
                        label: <Space><UploadOutlined/>{t("metadata-entity-imports", "Metadata Entity Imports")}</Space>,
                        children: (
                            <Space direction="vertical" style={{width: "100%"}} size="middle">
                                <div style={{display: "flex", justifyContent: "flex-end"}}>
                                    <NewMetadataEntityImportButton
                                        metadataEntityTypes={metadataEntityTypes}
                                        onCreate={loadData}
                                        onDone={loadData}
                                    />
                                </div>
                                <Table
                                    rowKey="id"
                                    size="middle"
                                    loading={loading}
                                    dataSource={metadataEntityImports}
                                    columns={metadataEntityImportColumns(t, loadData, metadataEntityTypes)}
                                    pagination={false}
                                />
                            </Space>
                        )
                    }
                ].filter(Boolean)}
            />

            <Modal
                open={modalOpen}
                title={modalTitle(editing, t)}
                onCancel={closeEditor}
                onOk={saveEditor}
                destroyOnClose
                width={720}
            >
                <Form form={form} layout="vertical" preserve={false}>
                    <Form.Item name="name" label={t("name", "Name")} rules={[{required: true}]}>
                        <Input/>
                    </Form.Item>
                    {editing?.kind !== "group" && (
                        <Form.Item name="slug" label={t("slug", "Slug")}>
                            <Input/>
                        </Form.Item>
                    )}
                    <Form.Item
                        name="icon"
                        label={t("icon", "Icon")}
                        normalize={value => value?.trim()}
                        extra={t("icon-emoji-hint", "Choose from the list, or type a single character/emoji (e.g. 📋).")}
                    >
                        <AutoComplete
                            allowClear
                            options={configurableIconSelectOptions()}
                            placeholder={t("icon-placeholder", "Choose an icon or type an emoji")}
                            filterOption={(input, option) => option.value.toLowerCase().includes(input.toLowerCase())}
                        />
                    </Form.Item>
                    <Form.Item name="description" label={t("description", "Description")}>
                        <Input.TextArea rows={3}/>
                    </Form.Item>
                    <Form.Item name="enabled" label={t("enabled", "Enabled")} valuePropName="checked">
                        <Switch/>
                    </Form.Item>
                    {editing?.kind === "entityType" && (
                        <Form.Item
                            name="main_text_editable"
                            label={t("main-text-editable", "Main Text editable")}
                            valuePropName="checked"
                            extra={t("main-text-editable-help", "When disabled, Main Text remains visible but cannot be edited after the asset is created.")}
                        >
                            <Switch/>
                        </Form.Item>
                    )}

                    {editing?.kind !== "group" && (
                        <Form.Item name="group_ids" label={t("field-groups", "Field Groups")}>
                            <Select mode="multiple" options={groupOptions} showSearch optionFilterProp="label"/>
                        </Form.Item>
                    )}

                    {editing?.kind === "metadataEntityType" && (
                        <Form.Item
                            name="standard_field_keys"
                            label={t("standard-metadata-fields", "Standard Metadata Fields")}
                        >
                            <Select
                                mode="multiple"
                                options={STANDARD_METADATA_FIELD_OPTIONS}
                                showSearch
                                optionFilterProp="label"
                            />
                        </Form.Item>
                    )}

                    <Form.Item name="field_ids" label={editing?.kind === "group" ? t("fields", "Fields") : t("ungrouped-fields", "Ungrouped Fields")}>
                        <Select mode="multiple" options={fieldOptions} showSearch optionFilterProp="label"/>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                open={assetModalOpen}
                title={creatingEntityType && t("create-entity-asset", "Create {{name}}", {name: creatingEntityType.name})}
                onCancel={closeAssetCreator}
                onOk={saveAssetCreator}
                okText={t("create", "Create")}
                okButtonProps={{disabled: !assetFormSubmittable || creatingAsset, loading: creatingAsset}}
                destroyOnClose
                width={760}
            >
                <Form form={assetForm} layout="vertical" preserve={false} onValuesChange={updateAssetCreatorSubmittable}>
                    <Form.Item name="filename" label={t("filename-label", "Filename / label")} rules={[{required: true, whitespace: true}]}>
                        <Input/>
                    </Form.Item>
                    <Form.Item name="content" label={t("main-text", "Main Text")}>
                        <Input.TextArea rows={4}/>
                    </Form.Item>
                    <EntityAssetFormFields entityType={creatingEntityType}/>
                </Form>
            </Modal>
        </>
    );
}

function SchemaFieldList({description, fields}) {
    const {t} = useTranslation();

    return (
        <Space direction="vertical" size={4} style={{maxWidth: 320}}>
            {description && <Text type="secondary">{description}</Text>}
            {fields.length ? fields.map(field => (
                <div key={field.id}>
                    {field.name}{" "}
                    <Text type="secondary">
                        ({field.value_type}{field.free ? "" : `, ${t("controlled", "controlled")}`}{field.multi ? `, ${t("multi", "multi")}` : ""})
                    </Text>
                </div>
            )) : <Text type="secondary">{t("no-fields", "No fields")}</Text>}
        </Space>
    );
}

function GroupTag({membership}) {
    const group = membership.custom_meta_group;
    if (!group) return null;

    const fields = (group.custom_meta_fields || [])
        .map(fieldMembership => fieldMembership.custom_meta_field)
        .filter(Boolean);

    return (
        <Popover
            title={<>{group.icon && <ConfigurableEntityIcon icon={group.icon}/>}{group.name}</>}
            content={<SchemaFieldList description={group.description} fields={fields}/>}
        >
            <Tag style={{cursor: "default"}}>
                {group.icon && <ConfigurableEntityIcon icon={group.icon}/>}
                {group.name}
            </Tag>
        </Popover>
    );
}

function ConfigurableTypeTag({type, kind}) {
    if (!type) return null;

    const fields = kind === "metadata_entity" ? metadataEntityTypeFields(type) : entityTypeFields(type);

    return (
        <Popover
            title={<><ConfigurableEntityIcon icon={type.icon} kind={kind}/>{type.name}</>}
            content={<SchemaFieldList description={type.description} fields={fields}/>}
        >
            <Tag style={{cursor: "default"}}>
                <ConfigurableEntityIcon icon={type.icon} kind={kind}/>
                {type.name}
            </Tag>
        </Popover>
    );
}

function ConfigTable({data, loading, columns, extra}) {
    return (
        <Space direction="vertical" style={{width: "100%"}} size="middle">
            <div style={{display: "flex", justifyContent: "flex-end"}}>{extra}</div>
            <Table
                rowKey="id"
                size="middle"
                loading={loading}
                dataSource={data}
                columns={columns}
                pagination={false}
            />
        </Space>
    );
}

function ImportStateTag({state}) {
    const {t} = useTranslation();

    switch (state) {
        case "pending":
            return <Tag icon={<ClockCircleOutlined/>}>{t("pending", "Pending")}</Tag>;
        case "analyzing":
            return <Tag color="blue" icon={<LoadingOutlined/>}>{t("analyzing", "Analyzing")}</Tag>;
        case "analyzed":
            return <Tag color="gold" icon={<FileSearchOutlined/>}>{t("analyzed", "Analyzed")}</Tag>;
        case "processing":
            return <Tag color="blue" icon={<LoadingOutlined/>}>{t("processing", "Processing")}</Tag>;
        case "processed":
            return <Tag color="green" icon={<CheckCircleOutlined/>}>{t("processed", "Processed")}</Tag>;
        case "errored":
            return <Tag color="red" icon={<AlertOutlined/>}>{t("errored", "Errored")}</Tag>;
        default:
            return <Tag>{state}</Tag>;
    }
}

function NewAssetImportButton({entityTypes, onCreate, onDone}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [entityTypeId, setEntityTypeId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [assetImport, setAssetImport] = useState(null);

    const onClick = () => {
        setAssetImport(null);
        setEntityTypeId(null);
        setOpen(true);
    };

    const close = () => {
        setOpen(false);
        setAssetImport(null);
    };

    const onAddFile = (file, storageFolderId) => {
        const formData = new FormData();
        formData.append("asset_spreadsheet_import[entity_type_id]", entityTypeId);
        if (storageFolderId) formData.append("asset_spreadsheet_import[storage_folder_id]", storageFolderId);
        formData.append("asset_spreadsheet_import[file]", file);

        setUploading(true);
        api.post(typeConfig.assetImport.endpoint, formData).then(res => {
            setUploading(false);
            setAssetImport(res.data);
            onCreate && onCreate(res.data);
        }).catch(error => {
            setUploading(false);
            handleApiError(error);
        });

        return false;
    };

    return (<>
        <Button type="primary" icon={<PlusOutlined/>} onClick={onClick}>
            {t("button-new-import", "New Import")}
        </Button>
        <Modal
            title={t("new-text-asset-import", "New Text Asset Import")}
            open={open}
            onCancel={close}
            footer={null}
            destroyOnClose
        >
            {assetImport ? (
                <AssetSpreadsheetImportSetup
                    assetImport={assetImport}
                    onDone={() => {
                        close();
                        onDone && onDone();
                    }}
                />
            ) : (
                <Formik
                    initialValues={{storage_folder_id: null}}
                    enableReinitialize
                    onSubmit={() => {}}
                >
                    {({values, setFieldValue}) => (
                        <Space direction="vertical" style={{width: "100%"}}>
                            <Select
                                style={{width: "100%"}}
                                placeholder={t("choose-text-asset-type", "Choose text asset type")}
                                value={entityTypeId}
                                onChange={setEntityTypeId}
                                options={entityTypes.map(entityType => ({value: entityType.id, label: entityType.name}))}
                            />
                            <div>
                                <Space style={{width: "100%", justifyContent: "space-between"}}>
                                    <Text strong>{t("choose-import-target-folder-optional", "Target folder (optional)")}</Text>
                                    <Button
                                        type="link"
                                        size="small"
                                        disabled={!values.storage_folder_id}
                                        onClick={() => setFieldValue("storage_folder_id", null)}
                                    >
                                        {t("clear", "Clear")}
                                    </Button>
                                </Space>
                                <div style={{border: "1px solid #d9d9d9", borderRadius: 6, padding: 12, maxHeight: 320, overflow: "auto"}}>
                                    <AssetGroupChooser
                                        type="storage_folders"
                                        fieldName="storage_folder_id"
                                        sessionKey="asset-spreadsheet-import-target-folder"
                                        draggable={false}
                                    />
                                </div>
                            </div>
                            <Upload beforeUpload={file => onAddFile(file, values.storage_folder_id)} showUploadList={false} accept=".csv,.tsv,.xls,.xlsx,.xml">
                                <Button icon={<UploadOutlined/>} loading={uploading} disabled={!entityTypeId} block>
                                    {uploading ? t("uploading", "Uploading...") : t("select-file", "Select File...")}
                                </Button>
                            </Upload>
                            <Text type="secondary"><em>{t("asset-import-supported-filetypes", "Supported file types: .csv, .tsv, .xls, .xlsx, .xml")}</em></Text>
                        </Space>
                    )}
                </Formik>
            )}
        </Modal>
    </>);
}

function AssetImportActions({assetImport, onChanged}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);

    const destroy = () => {
        api.delete(`${typeConfig.assetImport.endpoint}/${assetImport.id}`).then(() => {
            message.success(t("asset-import-canceled", "Import canceled."));
            onChanged && onChanged();
        }).catch(handleApiError);
    };

    return (
        <Space size={5}>
            {assetImport.aasm_state === "analyzed" && (<>
                <Button size="small" icon={<SettingOutlined/>} onClick={() => setOpen(true)}>
                    {t("button-process", "Process")}...
                </Button>
                <Modal
                    title={t("process-text-asset-import", "Process Text Asset Import")}
                    open={open}
                    onCancel={() => setOpen(false)}
                    footer={null}
                    destroyOnClose
                >
                    <AssetSpreadsheetImportSetup
                        assetImport={assetImport}
                        onDone={() => {
                            setOpen(false);
                            onChanged && onChanged();
                        }}
                    />
                </Modal>
            </>)}
            {assetImport.download_url && (
                <Tooltip title={t("download-uploaded-file", "Download uploaded file")}>
                    <Button
                        size="small"
                        icon={<DownloadOutlined/>}
                        href={assetImport.download_url}
                        download={assetImport.filename}
                        target="_blank"
                        rel="noopener noreferrer"
                    />
                </Tooltip>
            )}
            {!["processing", "processed"].includes(assetImport.aasm_state) && (
                <Popconfirm
                    title={t("confirm-cancel-import", "Cancel this import?")}
                    onConfirm={destroy}
                >
                    <Button size="small" danger icon={<DeleteOutlined/>}/>
                </Popconfirm>
            )}
        </Space>
    );
}

function MappedColumnsCell({record, labelFor}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const columns = record.columns || [];
    const mappedCount = columns.filter(column => column.mapping).length;

    if (!columns.length) return <Text type="secondary">0/0</Text>;

    return (<>
        <Button type="link" size="small" style={{padding: 0}} onClick={() => setOpen(true)}>
            {mappedCount}/{columns.length}
        </Button>
        <Modal
            title={`${t("column-mappings", "Column Mappings")}: ${record.filename}`}
            open={open}
            onCancel={() => setOpen(false)}
            footer={null}
            destroyOnClose
        >
            <Table
                rowKey={column => `${record.id}-${column.index}-${column.name}`}
                size="small"
                dataSource={columns}
                pagination={false}
                columns={[
                    {
                        title: t("source-column", "Source Column"),
                        dataIndex: "name"
                    },
                    {
                        title: t("mapping", "Mapping"),
                        render: (_, column) => column.mapping ?
                            labelFor(column, t) :
                            <Text type="secondary">{labelFor(column, t)}</Text>
                    }
                ]}
            />
        </Modal>
    </>);
}

function assetImportColumns(t, onChanged, entityTypes) {
    return [
        {
            title: t("file", "File"),
            dataIndex: "filename",
            render: filename => <Text strong>{filename}</Text>
        },
        {
            title: t("type", "Type"),
            dataIndex: "entity_type_id",
            width: 180,
            filters: (entityTypes || []).map(entityType => ({text: entityType.name, value: entityType.id})),
            onFilter: (value, record) => record.entity_type_id === value,
            render: (_, record) => <ConfigurableTypeTag type={record.entity_type} kind="text_asset"/>
        },
        {
            title: t("target-folder", "Target Folder"),
            dataIndex: "storage_folder",
            width: 180,
            render: folder => folder?.path_names?.join(" / ") || folder?.name || null
        },
        {
            title: t("created", "Created"),
            dataIndex: "created_at",
            width: 140,
            render: date => <TimeAgo date={date}/>
        },
        {
            title: t("created-by", "Created By"),
            dataIndex: "user",
            width: 160,
            render: user => user && <User user={user}/>
        },
        {
            title: t("rows", "Rows"),
            dataIndex: "rows",
            width: 90
        },
        {
            title: t("state", "State"),
            dataIndex: "aasm_state",
            width: 120,
            render: state => <ImportStateTag state={state}/>
        },
        {
            title: t("mapped-columns", "Mapped Columns"),
            key: "mapped-columns",
            width: 150,
            render: (_, record) => (
                <MappedColumnsCell
                    record={record}
                    labelFor={(column, tFn) => mappingLabel(record.entity_type, column, tFn)}
                />
            )
        },
        {
            title: t("assets", "Assets"),
            dataIndex: "assets_count",
            width: 90,
            render: (assetsCount, record) => (
                <OrgNavLink to={`/explore?asset_spreadsheet_import_id=${record.id}`}>
                    {assetsCount || 0}
                </OrgNavLink>
            )
        },
        {
            title: "",
            key: "actions",
            width: 200,
            render: (_, record) => <AssetImportActions assetImport={record} onChanged={onChanged}/>
        }
    ];
}

function NewMetadataEntityImportButton({metadataEntityTypes, onCreate, onDone}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [metadataEntityTypeId, setMetadataEntityTypeId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [metadataImport, setMetadataImport] = useState(null);

    const onClick = () => {
        setMetadataImport(null);
        setMetadataEntityTypeId(null);
        setOpen(true);
    };

    const close = () => {
        setOpen(false);
        setMetadataImport(null);
    };

    const onAddFile = file => {
        const formData = new FormData();
        formData.append("metadata_entity_import[metadata_entity_type_id]", metadataEntityTypeId);
        formData.append("metadata_entity_import[file]", file);

        setUploading(true);
        api.post(typeConfig.metadataImport.endpoint, formData).then(res => {
            setUploading(false);
            setMetadataImport(res.data);
            onCreate && onCreate(res.data);
        }).catch(error => {
            setUploading(false);
            handleApiError(error);
        });

        return false;
    };

    return (<>
        <Button type="primary" icon={<PlusOutlined/>} onClick={onClick}>
            {t("button-new-import", "New Import")}
        </Button>
        <Modal
            title={t("new-metadata-entity-import", "New Metadata Entity Import")}
            open={open}
            onCancel={close}
            footer={null}
            destroyOnClose
        >
            {metadataImport ? (
                <MetadataEntityImportSetup
                    metadataImport={metadataImport}
                    onDone={() => {
                        close();
                        onDone && onDone();
                    }}
                />
            ) : (
                <Space direction="vertical" style={{width: "100%"}}>
                    <Select
                        style={{width: "100%"}}
                        placeholder={t("choose-metadata-entity-type", "Choose metadata entity type")}
                        value={metadataEntityTypeId}
                        onChange={setMetadataEntityTypeId}
                        options={metadataEntityTypes.map(metadataEntityType => ({value: metadataEntityType.id, label: metadataEntityType.name}))}
                    />
                    <Upload beforeUpload={onAddFile} showUploadList={false} accept=".csv,.tsv,.xls,.xlsx">
                        <Button icon={<UploadOutlined/>} loading={uploading} disabled={!metadataEntityTypeId} block>
                            {uploading ? t("uploading", "Uploading...") : t("select-file", "Select File...")}
                        </Button>
                    </Upload>
                    <Text type="secondary"><em>{t("metadata-import-supported-filetypes", "Supported file types: .csv, .tsv, .xls, .xlsx")}</em></Text>
                </Space>
            )}
        </Modal>
    </>);
}

function MetadataEntityImportActions({metadataImport, onChanged}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);

    const destroy = () => {
        api.delete(`${typeConfig.metadataImport.endpoint}/${metadataImport.id}`).then(() => {
            message.success(t("metadata-entity-import-canceled", "Import canceled."));
            onChanged && onChanged();
        }).catch(handleApiError);
    };

    return (
        <Space size={5}>
            {metadataImport.aasm_state === "analyzed" && (<>
                <Button size="small" icon={<SettingOutlined/>} onClick={() => setOpen(true)}>
                    {t("button-process", "Process")}...
                </Button>
                <Modal
                    title={t("process-metadata-entity-import", "Process Metadata Entity Import")}
                    open={open}
                    onCancel={() => setOpen(false)}
                    footer={null}
                    destroyOnClose
                >
                    <MetadataEntityImportSetup
                        metadataImport={metadataImport}
                        onDone={() => {
                            setOpen(false);
                            onChanged && onChanged();
                        }}
                    />
                </Modal>
            </>)}
            {metadataImport.download_url && (
                <Tooltip title={t("download-uploaded-file", "Download uploaded file")}>
                    <Button
                        size="small"
                        icon={<DownloadOutlined/>}
                        href={metadataImport.download_url}
                        download={metadataImport.filename}
                        target="_blank"
                        rel="noopener noreferrer"
                    />
                </Tooltip>
            )}
            {!["processing", "processed"].includes(metadataImport.aasm_state) && (
                <Popconfirm
                    title={t("confirm-cancel-import", "Cancel this import?")}
                    onConfirm={destroy}
                >
                    <Button size="small" danger icon={<DeleteOutlined/>}/>
                </Popconfirm>
            )}
        </Space>
    );
}

function metadataEntityImportColumns(t, onChanged, metadataEntityTypes) {
    return [
        {
            title: t("file", "File"),
            dataIndex: "filename",
            render: filename => <Text strong>{filename}</Text>
        },
        {
            title: t("type", "Type"),
            dataIndex: "metadata_entity_type_id",
            width: 180,
            filters: (metadataEntityTypes || []).map(metadataEntityType => ({text: metadataEntityType.name, value: metadataEntityType.id})),
            onFilter: (value, record) => record.metadata_entity_type_id === value,
            render: (_, record) => <ConfigurableTypeTag type={record.metadata_entity_type} kind="metadata_entity"/>
        },
        {
            title: t("created", "Created"),
            dataIndex: "created_at",
            width: 140,
            render: date => <TimeAgo date={date}/>
        },
        {
            title: t("created-by", "Created By"),
            dataIndex: "user",
            width: 160,
            render: user => user && <User user={user}/>
        },
        {title: t("rows", "Rows"), dataIndex: "rows", width: 90},
        {
            title: t("state", "State"),
            dataIndex: "aasm_state",
            width: 120,
            render: state => <ImportStateTag state={state}/>
        },
        {
            title: t("mapped-columns", "Mapped Columns"),
            key: "mapped-columns",
            width: 150,
            render: (_, record) => (
                <MappedColumnsCell
                    record={record}
                    labelFor={(column, tFn) => metadataEntityMappingLabel(record.metadata_entity_type, column, tFn)}
                />
            )
        },
        {
            title: t("entities", "Entities"),
            dataIndex: "entities_count",
            width: 100,
            render: (entitiesCount, record) => (
                <Tooltip
                    title={`${t("created", "Created")}: ${record.created_count || 0} · ${t("updated", "Updated")}: ${record.updated_count || 0} · ${t("skipped", "Skipped")}: ${record.skipped_count || 0}`}
                >
                    <span>
                        <OrgNavLink to={`/manage/taxonomy#browse-metadata-entity-${record.metadata_entity_type_id}`}>
                            {entitiesCount || 0}
                        </OrgNavLink>
                    </span>
                </Tooltip>
            )
        },
        {
            title: "",
            key: "actions",
            width: 160,
            render: (_, record) => <MetadataEntityImportActions metadataImport={record} onChanged={onChanged}/>
        }
    ];
}

function nestedMemberships(existingMemberships, selectedIds, targetKey, getTargetId) {
    const selected = selectedIds.map(id => parseInt(id, 10));
    const existingByTargetId = new Map(existingMemberships.map(membership => [getTargetId(membership), membership]));
    const selectedAttributes = selected.map((id, index) => {
        const existing = existingByTargetId.get(id);
        return {
            ...(existing?.id ? {id: existing.id} : {}),
            [targetKey]: id,
            position: index + 1
        };
    });

    const removedAttributes = existingMemberships
        .filter(membership => !selected.includes(getTargetId(membership)))
        .map(membership => ({
            id: membership.id,
            [targetKey]: getTargetId(membership),
            _destroy: true
        }));

    return [...selectedAttributes, ...removedAttributes];
}

function modalTitle(editing, t) {
    if (!editing) return "";
    const prefix = editing.record ? t("edit", "Edit") : t("new", "New");
    return `${prefix} ${t(typeConfig[editing.kind].titleKey, typeConfig[editing.kind].title)}`;
}
